import React, { useState, useCallback, useEffect } from 'react';
import {
  Button,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress
} from '@mui/material';
import DialogModal from '../../../components/DialogModal';
import OverlayLoader from '../../../components/OverlayLoader/OverlayLoader';
import {
  StudentPaymentSchemeWithMetrics,
  PaymentAddons,
  AdditionalConcessionsAndWaivers,
  PaymentAddonsStatus,
  ConcessionOrWaiverUpdateRequest
} from '../../../types/paymentScheme';
import { SnackbarSeverityType } from '../../../enums';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { dataGridSx } from '../../common/styled/DataGrid';
import pallet from '../../../layouts/pallet';
import tableColumnUtils from '../../../utils/tableColumnUtils';
import paymentSchemesApis from '../../../apis/payments/paymentSchemes';
import { normalizeSelection } from '../../../utils/gridSelection';

interface ApproveRejectConcessionWaiverDialogProps {
  open: boolean;
  onClose: () => void;
  studentId: string;
  onShowToast: (message: string, severity: SnackbarSeverityType) => void;
  onUpdate: () => void;
  allPaymentSchemes: StudentPaymentSchemeWithMetrics[];
  selectedPaymentScheme: StudentPaymentSchemeWithMetrics | null;
  isLoadingFeeDetails: boolean;
  sourceTable?: 'feeDetails' | 'feeFineDetails';
}

interface ConcessionWaiverRow extends PaymentAddons {
  id: string;
  academicYear: string;
  feePeriod: string;
  particulars: string;
  schemeId: string;
}

interface AdditionalConcessionWaiverRow
  extends AdditionalConcessionsAndWaivers {
  id: string;
  academicYear: string;
  feePeriod: string;
  particulars: string;
  schemeId: string;
}

const ApproveRejectConcessionWaiverDialog: React.FC<
  ApproveRejectConcessionWaiverDialogProps
> = ({
  open,
  onClose,
  studentId,
  onShowToast,
  onUpdate,
  allPaymentSchemes,
  selectedPaymentScheme,
  isLoadingFeeDetails,
  sourceTable
}) => {
  const [showApproved, setShowApproved] = useState(false);
  const [selectedConcessions, setSelectedConcessions] = useState<string[]>([]);
  const [selectedWaivers, setSelectedWaivers] = useState<string[]>([]);
  const [selectedLateFeeConcessions, setSelectedLateFeeConcessions] = useState<
    string[]
  >([]);
  const [selectedLateFeeWaivers, setSelectedLateFeeWaivers] = useState<
    string[]
  >([]);
  const [showLoader, setShowLoader] = useState(false);

  const resetState = useCallback(() => {
    setShowApproved(false);
    setSelectedConcessions([]);
    setSelectedWaivers([]);
    setSelectedLateFeeConcessions([]);
    setSelectedLateFeeWaivers([]);
    setShowLoader(false);
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  const isLoading = isLoadingFeeDetails;

  const isEmpty = (arr: any[] | undefined | null) => !arr || arr.length === 0;

  const schemesToProcess = selectedPaymentScheme
    ? [selectedPaymentScheme]
    : allPaymentSchemes;

  const filterDataByStatus = <
    T extends PaymentAddons | AdditionalConcessionsAndWaivers
  >(
    data: T[],
    statusType: PaymentAddonsStatus
  ): T[] => {
    return data.filter((item) => item.status === statusType);
  };

  const concessionsData: ConcessionWaiverRow[] = [];
  const waiversData: ConcessionWaiverRow[] = [];
  const lateFeeConcessionsData: AdditionalConcessionWaiverRow[] = [];
  const lateFeeWaiversData: AdditionalConcessionWaiverRow[] = [];

  schemesToProcess.forEach((scheme) => {
    const statusToFilter = showApproved
      ? PaymentAddonsStatus.APPROVED
      : PaymentAddonsStatus.IN_APPROVAL;

    filterDataByStatus(scheme.concessions || [], statusToFilter).forEach(
      (concession) => {
        concessionsData.push({
          academicYear: scheme.academicYear,
          feePeriod: scheme.feePeriod,
          particulars: scheme.particulars,
          schemeId: scheme._id,
          ...concession
        });
      }
    );

    filterDataByStatus(scheme.waivers || [], statusToFilter).forEach(
      (waiver) => {
        waiversData.push({
          academicYear: scheme.academicYear,
          feePeriod: scheme.feePeriod,
          particulars: scheme.particulars,
          schemeId: scheme._id,
          ...waiver
        });
      }
    );

    filterDataByStatus(
      scheme.additionalConcessionsAndWaivers || [],
      statusToFilter
    ).forEach((additional) => {
      if (additional.type === 'CONCESSIONS') {
        lateFeeConcessionsData.push({
          academicYear: scheme.academicYear,
          feePeriod: scheme.feePeriod,
          particulars: scheme.particulars,
          schemeId: scheme._id,
          ...additional
        });
      } else if (additional.type === 'WAIVERS') {
        lateFeeWaiversData.push({
          academicYear: scheme.academicYear,
          feePeriod: scheme.feePeriod,
          particulars: scheme.particulars,
          schemeId: scheme._id,
          ...additional
        });
      }
    });
  });

  const commonColumns: GridColDef[] = [
    { field: 'academicYear', headerName: 'Academic Year', flex: 1 },
    { field: 'feePeriod', headerName: 'Fee Period', flex: 1 },
    { field: 'particulars', headerName: 'Particulars', flex: 1 },
    { field: 'description', headerName: 'Description', flex: 1 },
    { field: 'amount', headerName: 'Amount', type: 'number', flex: 1 },
    { field: 'status', headerName: 'Status', flex: 1 }
  ];

  const additionalColumns: GridColDef[] = [
    { field: 'academicYear', headerName: 'Academic Year', flex: 1 },
    { field: 'feePeriod', headerName: 'Fee Period', flex: 1 },
    { field: 'particulars', headerName: 'Particulars', flex: 1 },
    { field: 'description', headerName: 'Description', flex: 1 },
    { field: 'amount', headerName: 'Amount', type: 'number', flex: 1 },
    { field: 'appliedOn', headerName: 'Applied On', flex: 1 },
    { field: 'status', headerName: 'Status', flex: 1 }
  ];

  const createUpdateRequest = (status: PaymentAddonsStatus) => {
    const uniquePaymentSchemeIds = new Set<string>();

    const multipleConcessionsTableSelectedData = concessionsData.filter((row) =>
      selectedConcessions.includes(row.id)
    );
    const multipleWaiversSelectedData = waiversData.filter((row) =>
      selectedWaivers.includes(row.id)
    );
    const multipleAdditionalConcessionsTableSelectedData =
      lateFeeConcessionsData.filter((row) =>
        selectedLateFeeConcessions.includes(row.id)
      );
    const multipleAdditionalWaiversSelectedData = lateFeeWaiversData.filter(
      (row) => selectedLateFeeWaivers.includes(row.id)
    );

    multipleConcessionsTableSelectedData.forEach((row) =>
      uniquePaymentSchemeIds.add(row.schemeId)
    );
    multipleWaiversSelectedData.forEach((row) =>
      uniquePaymentSchemeIds.add(row.schemeId)
    );
    multipleAdditionalConcessionsTableSelectedData.forEach((row) =>
      uniquePaymentSchemeIds.add(row.schemeId)
    );
    multipleAdditionalWaiversSelectedData.forEach((row) =>
      uniquePaymentSchemeIds.add(row.schemeId)
    );

    const updateRequest: ConcessionOrWaiverUpdateRequest[] = [];

    Array.from(uniquePaymentSchemeIds).forEach((paymentSchemeId) => {
      const paymentSchemeWiseConcessions =
        multipleConcessionsTableSelectedData.filter(
          (concession) => concession.schemeId === paymentSchemeId
        );
      const paymentSchemeWiseWaivers = multipleWaiversSelectedData.filter(
        (waiver) => waiver.schemeId === paymentSchemeId
      );
      const paymentSchemeWiseAdditionalConcessions =
        multipleAdditionalConcessionsTableSelectedData.filter(
          (concession) => concession.schemeId === paymentSchemeId
        );
      const paymentSchemeWiseAdditionalWaivers =
        multipleAdditionalWaiversSelectedData.filter(
          (waiver) => waiver.schemeId === paymentSchemeId
        );

      const concessionsRequests = paymentSchemeWiseConcessions.map(
        (concession) => ({
          description: concession.description,
          amount: concession.amount,
          id: concession.id,
          status: status
        })
      );

      const waiversRequests = paymentSchemeWiseWaivers.map((waiver) => ({
        description: waiver.description,
        amount: waiver.amount,
        id: waiver.id,
        status: status
      }));

      const additionalConcessionsRequests =
        paymentSchemeWiseAdditionalConcessions.map((concession) => ({
          description: concession.description,
          amount: concession.amount,
          id: concession.id,
          status: status,
          type: concession.type,
          appliedOn: concession.appliedOn
        }));

      const additionalWaiversRequests = paymentSchemeWiseAdditionalWaivers.map(
        (waiver) => ({
          description: waiver.description,
          amount: waiver.amount,
          id: waiver.id,
          status: status,
          type: waiver.type,
          appliedOn: waiver.appliedOn
        })
      );

      if (
        !isEmpty(concessionsRequests) ||
        !isEmpty(waiversRequests) ||
        !isEmpty(additionalConcessionsRequests) ||
        !isEmpty(additionalWaiversRequests)
      ) {
        updateRequest.push({
          paymentSchemeId: paymentSchemeId,
          concessions: concessionsRequests,
          waivers: waiversRequests,
          additionalConcessionsAndWaivers: [
            ...additionalConcessionsRequests,
            ...additionalWaiversRequests
          ]
        });
      }
    });
    return updateRequest;
  };

  const handleApprove = async () => {
    const updateRequest = createUpdateRequest(PaymentAddonsStatus.APPROVED);

    try {
      setShowLoader(true);
      await paymentSchemesApis.updateMultipleConcessionsAndWaiversStatus({
        concessionOrWaversUpdateRequests: updateRequest
      });
      onShowToast(
        'Selected concessions/waivers approved successfully.',
        SnackbarSeverityType.success
      );
      onUpdate();
      onClose();
      resetState();
    } catch (error: any) {
      console.error('Error approving concessions/waivers:', error);
      onShowToast(
        error?.errors?.[0]?.message || 'Failed to approve concessions/waivers.',
        SnackbarSeverityType.error
      );
    } finally {
      setShowLoader(false);
    }
  };

  const handleReject = async () => {
    const updateRequest = createUpdateRequest(PaymentAddonsStatus.REJECTED);

    try {
      setShowLoader(true);
      await paymentSchemesApis.updateMultipleConcessionsAndWaiversStatus({
        concessionOrWaversUpdateRequests: updateRequest
      });
      onShowToast(
        'Selected concessions/waivers rejected successfully.',
        SnackbarSeverityType.success
      );
      onUpdate();
      onClose();
      resetState();
    } catch (error: any) {
      console.error('Error rejecting concessions/waivers:', error);
      onShowToast(
        error?.errors?.[0]?.message || 'Failed to reject concessions/waivers.',
        SnackbarSeverityType.error
      );
    } finally {
      setShowLoader(false);
    }
  };

  const dialogActions = (
    <Box display='flex' gap='16px'>
      {!showApproved && (
        <Button
          variant='contained'
          color='primary'
          onClick={handleApprove}
          sx={{ textTransform: 'none' }}
          disabled={
            selectedConcessions.length === 0 &&
            selectedWaivers.length === 0 &&
            selectedLateFeeConcessions.length === 0 &&
            selectedLateFeeWaivers.length === 0
          }
        >
          Approve
        </Button>
      )}
      <Button
        variant='contained'
        color='error'
        onClick={handleReject}
        sx={{ textTransform: 'none' }}
        disabled={
          selectedConcessions.length === 0 &&
          selectedWaivers.length === 0 &&
          selectedLateFeeConcessions.length === 0 &&
          selectedLateFeeWaivers.length === 0
        }
      >
        Reject
      </Button>
    </Box>
  );

  return (
    <>
      <OverlayLoader show={showLoader} />
      <DialogModal
        open={open}
        onClose={onClose}
        title='Approve/Reject - Concessions/Waivers'
        maxWidth='lg'
        actions={dialogActions}
      >
        <Box mb={2} display='flex' alignItems='center'>
          <FormControlLabel
            control={
              <Switch
                checked={showApproved}
                onChange={() => setShowApproved(!showApproved)}
                name='showApproved'
                color='primary'
              />
            }
            label='Show Approved Concessions And Waivers'
          />
          {isLoading && <CircularProgress size={20} sx={{ ml: 2 }} />}
        </Box>

        {(sourceTable === 'feeDetails' || sourceTable === undefined) && (
          <>
            {!isEmpty(concessionsData) && (
              <>
                <Typography variant='h6' mb={1}>
                  Concessions
                </Typography>
                <Box sx={{ height: 'auto', width: '100%', mb: 3 }}>
                  <DataGrid
                    rows={concessionsData}
                    columns={commonColumns}
                    loading={isLoading}
                    checkboxSelection
                    onRowSelectionModelChange={(newSelection: any) => {
                      setSelectedConcessions(
                        normalizeSelection(
                          newSelection,
                          concessionsData.map((row) => row.id)
                        )
                      );
                    }}
                    getRowId={(row) => row.id}
                    disableColumnMenu
                    pageSizeOptions={tableColumnUtils.PAGE_SIZE_OPTIONS}
                    initialState={{
                      ...tableColumnUtils.DATA_GRID_INITIAL_STATE,
                      pagination: {
                        paginationModel: { page: 0, pageSize: 5 }
                      }
                    }}
                    sx={{
                      ...dataGridSx(pallet),
                      height: 200,
                      maxHeight: 200,
                      '& .MuiDataGrid-virtualScroller': {
                        overflowY: 'auto'
                      }
                    }}
                  />
                </Box>
              </>
            )}

            {!isEmpty(waiversData) && (
              <>
                <Typography variant='h6' mb={1}>
                  Waivers
                </Typography>
                <Box sx={{ height: 'auto', width: '100%', mb: 3 }}>
                  <DataGrid
                    rows={waiversData}
                    columns={commonColumns}
                    loading={isLoading}
                    checkboxSelection
                    onRowSelectionModelChange={(newSelection: any) => {
                      setSelectedWaivers(
                        normalizeSelection(
                          newSelection,
                          waiversData.map((row) => row.id)
                        )
                      );
                    }}
                    getRowId={(row) => row.id}
                    disableColumnMenu
                    pageSizeOptions={tableColumnUtils.PAGE_SIZE_OPTIONS}
                    initialState={{
                      ...tableColumnUtils.DATA_GRID_INITIAL_STATE,
                      pagination: {
                        paginationModel: { page: 0, pageSize: 5 }
                      }
                    }}
                    sx={{
                      ...dataGridSx(pallet),
                      height: 200,
                      maxHeight: 200,
                      '& .MuiDataGrid-virtualScroller': {
                        overflowY: 'auto'
                      }
                    }}
                  />
                </Box>
              </>
            )}
          </>
        )}

        {(sourceTable === 'feeFineDetails' || sourceTable === undefined) && (
          <>
            {!isEmpty(lateFeeConcessionsData) && (
              <>
                <Typography variant='h6' mb={1}>
                  Late Fee Concessions
                </Typography>
                <Box sx={{ height: 'auto', width: '100%', mb: 3 }}>
                  <DataGrid
                    rows={lateFeeConcessionsData}
                    columns={additionalColumns}
                    loading={isLoading}
                    checkboxSelection
                    onRowSelectionModelChange={(newSelection: any) => {
                      setSelectedLateFeeConcessions(
                        normalizeSelection(
                          newSelection,
                          lateFeeConcessionsData.map((row) => row.id)
                        )
                      );
                    }}
                    getRowId={(row) => row.id}
                    disableColumnMenu
                    pageSizeOptions={tableColumnUtils.PAGE_SIZE_OPTIONS}
                    initialState={{
                      ...tableColumnUtils.DATA_GRID_INITIAL_STATE,
                      pagination: {
                        paginationModel: { page: 0, pageSize: 5 }
                      }
                    }}
                    sx={{
                      ...dataGridSx(pallet),
                      height: 200,
                      maxHeight: 200,
                      '& .MuiDataGrid-virtualScroller': {
                        overflowY: 'auto'
                      }
                    }}
                  />
                </Box>
              </>
            )}

            {!isEmpty(lateFeeWaiversData) && (
              <>
                <Typography variant='h6' mb={1}>
                  Late Fee Waivers
                </Typography>
                <Box sx={{ height: 'auto', width: '100%', mb: 3 }}>
                  <DataGrid
                    rows={lateFeeWaiversData}
                    columns={additionalColumns}
                    loading={isLoading}
                    checkboxSelection
                    onRowSelectionModelChange={(newSelection: any) => {
                      setSelectedLateFeeWaivers(
                        normalizeSelection(
                          newSelection,
                          lateFeeWaiversData.map((row) => row.id)
                        )
                      );
                    }}
                    getRowId={(row) => row.id}
                    disableColumnMenu
                    pageSizeOptions={tableColumnUtils.PAGE_SIZE_OPTIONS}
                    initialState={{
                      ...tableColumnUtils.DATA_GRID_INITIAL_STATE,
                      pagination: {
                        paginationModel: { page: 0, pageSize: 5 }
                      }
                    }}
                    sx={{
                      ...dataGridSx(pallet),
                      height: 200,
                      maxHeight: 200,
                      '& .MuiDataGrid-virtualScroller': {
                        overflowY: 'auto'
                      }
                    }}
                  />
                </Box>
              </>
            )}
          </>
        )}
      </DialogModal>
    </>
  );
};

export default ApproveRejectConcessionWaiverDialog;
